/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

import {vec3, quat, mat4} from "gl-matrix";

import * as math from "App/math";

/**
 * Abstract object with basic functions
 *
 * @constructor
 */
export default
class MovableObject {

    protected _modelMatrixValid: boolean = false;

    // Position and scale
    private _position: vec3 = vec3.fromValues(0.0, 0.0, 0.0);
    private _scale: vec3 = vec3.fromValues(1.0, 1.0, 1.0);
    private _rotation: quat = quat.create();
    private _modelMatrix: mat4 = mat4.create();

    // Additional data
    private _id: number = 0;
    private _manipulator: any = null;

    // Temporary data
    private _tmpVec3: vec3 = vec3.create();
    private _tmpQuat: quat = quat.create();

    constructor() {
        //
    }

    /**
     * Generate quaternion from euler angles
     * https://en.wikipedia.org/wiki/Conversion_between_quaternions_and_Euler_angles
     *
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @return {quat}
     * @protected
     */
    public _eulerToQuat(x: number, y: number, z: number): quat {
        var t0 = Math.cos(z * 0.5);
        var t1 = Math.sin(z * 0.5);
        var t2 = Math.cos(x * 0.5);
        var t3 = Math.sin(x * 0.5);
        var t4 = Math.cos(y * 0.5);
        var t5 = Math.sin(y * 0.5);

        return quat.set(this._tmpQuat,
            t0 * t3 * t4 - t1 * t2 * t5,
            t0 * t2 * t5 + t1 * t3 * t4,
            t1 * t2 * t4 - t0 * t3 * t5,
            t0 * t2 * t4 + t1 * t3 * t5
        );
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    public addPosition(x: number, y: number, z: number): void {
        if (math.isZero3(x, y, z)) {
            return;
        }

        this._position[0] += x;
        this._position[1] += y;
        this._position[2] += z;
        this._modelMatrixValid = false;
    }

    /**
     * @param {vec3} position
     */
    public addPositionVec(position: vec3): void {
        this.addPosition(position[0], position[1], position[2]);
    }

    /**
     * @param {number} position
     */
    public addPositionUni(position: number): void {
        this.addPosition(position, position, position);
    }

    /**
     * @param {number} angleX
     * @param {number} angleY
     * @param {number} angleZ
     * @param {boolean} [inWorld = true]
     */
    public addRotation(angleX: number, angleY: number, angleZ: number, inWorld: boolean): void {
        if (math.isZero3(angleX, angleY, angleZ)) {
            return;
        }

        this._modelMatrixValid = false;
        let qt = this._eulerToQuat(angleX, angleY, angleZ);

        if (inWorld === undefined || inWorld) {
            quat.multiply(this._rotation, qt, this._rotation);
        } else {
            quat.multiply(this._rotation, this._rotation, qt);
        }
    }

    /**
     * @param {vec3} angles
     * @param {boolean} [inWorld = true]
     */
    public addRotationVec(angles: vec3, inWorld: boolean): void {
        this.addRotation(angles[0], angles[1], angles[2], inWorld === undefined ? true : inWorld);
    }

    /**
     * @param {quat} qt
     * @param {boolean} [inWorld = true]
     */
    public addRotationQuat(qt: quat, inWorld: boolean): void {
        quat.identity(this._tmpQuat);
        if (quat.equals(qt, this._tmpQuat)) {
            return;
        }

        this._modelMatrixValid = false;
        if (inWorld === undefined || inWorld) {
            quat.multiply(this._rotation, qt, this._rotation);
        } else {
            quat.multiply(this._rotation, this._rotation, qt);
        }
    }

    /**
     * @param {number} angle
     * @param {boolean} [inWorld = true]
     */
    public addRotationUni(angle: number, inWorld: boolean): void {
        this.addRotation(angle, angle, angle, inWorld === undefined ? true : inWorld);
    }

    /**
     * @param {number} scaleX
     * @param {number} scaleY
     * @param {number} scaleZ
     */
    public addScale(scaleX: number, scaleY: number, scaleZ: number): void {
        if (math.isZero3(scaleX, scaleY, scaleZ)) {
            return;
        }

        this._scale[0] += scaleX;
        this._scale[1] += scaleY;
        this._scale[2] += scaleZ;
        this._modelMatrixValid = false;
    }

    /**
     * @param {vec3} scales
     */
    public addScaleVec(scales: vec3): void {
        this.addScale(scales[0], scales[1], scales[2]);
    }

    /**
     * @param {number} scale
     */
    public addScaleUni(scale: number): void {
        this.addScale(scale, scale, scale);
    }

    /**
     * @return {number}
     */
    public getID(): number {
        return this._id;
    }

    /**
     * @returns {mat4}
     */
    public getModelMatrix(): mat4 {
        if (!this._modelMatrixValid) {
            this._calculateModelMatrix();
            this._modelMatrixValid = true;
        }
        return this._modelMatrix;
    }

    /**
     * @returns {vec3}
     */
    public getPosition(): vec3 {
        return this._position;
    }

    /**
     * @returns {vec3}
     */
    public getScale(): vec3 {
        return this._scale;
    }

    /**
     * @returns {number}
     */
    public getScaleMin(): number {
        let min = this._scale[0];
        if (this._scale[1] < min) {
            min = this._scale[1];
        }
        if (this._scale[2] < min) {
            min = this._scale[2];
        }
        return min;
    }

    /**
     * @returns {number}
     */
    public getScaleMax(): number {
        let max = this._scale[0];
        if (this._scale[1] > max) {
            max = this._scale[1];
        }
        if (this._scale[2] > max) {
            max = this._scale[2];
        }
        return max;
    }

    /**
     * @returns {quat}
     */
    public getRotation(): quat {
        return this._rotation;
    }

    /**
     * @return {vec3}
     */
    public getRotationEuler(): vec3 {
        return this._quatToEuler(this._rotation);
    }

    /**
     * @param {number} id
     */
    public setID(id): void {
        this._id = id;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    public setPosition(x: number, y: number, z: number): void {
        if (vec3.equals(this._position, vec3.fromValues(x, y, z))) {
            return;
        }

        vec3.set(this._position, x, y, z);
        this._modelMatrixValid = false;
    }

    /**
     * @param {vec3} position
     */
    public setPositionVec(position: vec3): void {
        this.setPosition(position[0], position[1], position[2]);
    }

    /**
     * @param {number} position
     */
    public setPositionUni(position: number): void {
        this.setPosition(position, position, position);
    }

    /**
     * @param {number} x
     */
    public setPositionX(x: number): void {
        if (this._position[0] === x) {
            return;
        }
        this._position[0] = x;
        this._modelMatrixValid = false;
    }

    /**
     * @param {number} y
     */
    public setPositionY(y: number): void {
        if (this._position[1] === y) {
            return;
        }
        this._position[1] = y;
        this._modelMatrixValid = false;
    }

    /**
     * @param {number} z
     */
    public setPositionZ(z: number): void {
        if (this._position[2] === z) {
            return;
        }
        this._position[2] = z;
        this._modelMatrixValid = false;
    }

    /**
     * From euler angles
     *
     * @param {number} angleX
     * @param {number} angleY
     * @param {number} angleZ
     */
    public setRotation(angleX: number, angleY: number, angleZ: number): void {
        this._rotation = this._eulerToQuat(angleX, angleY, angleZ);
        this._modelMatrixValid = false;
    }

    /**
     * From euler angles
     *
     * @param {vec3} angles
     */
    public setRotationVec(angles: vec3): void {
        this.setRotation(angles[0], angles[1], angles[2]);
    }

    /**
     * @param {quat} qt
     */
    public setRotationQuat(qt: quat): void {
        if (quat.equals(this._rotation, qt)) {
            return;
        }

        quat.copy(this._rotation, qt);
        this._modelMatrixValid = false;
    }

    /**
     * @param {number} angle
     */
    public setRotationUni(angle: number): void {
        this.setRotation(angle, angle, angle);
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {number} z
     */
    public setScale(x: number, y: number, z: number): void {
        if (vec3.equals(this._scale, vec3.fromValues(x, y, z))) {
            return;
        }

        vec3.set(this._scale, x, y, z);
        this._modelMatrixValid = false;
    }

    /**
     * @param {vec3} scale
     */
    public setScaleVec(scale: vec3): void {
        this.setScale(scale[0], scale[1], scale[2]);
    }

    /**
     * @param {number} scale
     */
    public setScaleUni(scale: number): void {
        this.setScale(scale, scale, scale);
    }

    /**
     * @param {number} scale
     */
    public setScaleX(scale: number): void {
        if (math.equal(this._scale[0], scale)) {
            return;
        }
        this._scale[0] = scale;
        this._modelMatrixValid = false;
    }

    /**
     * @param {number} scale
     */
    public setScaleY(scale: number): void {
        if (math.equal(this._scale[1], scale)) {
            return;
        }
        this._scale[1] = scale;
        this._modelMatrixValid = false;
    }

    /**
     * @param {number} scale
     */
    public setScaleZ(scale: number): void {
        if (math.equal(this._scale[2], scale)) {
            return;
        }
        this._scale[2] = scale;
        this._modelMatrixValid = false;
    }

    /**
     * Recalculate model matrix
     *
     * @private
     */
    private _calculateModelMatrix(): void {
        mat4.fromRotationTranslationScale(this._modelMatrix, this._rotation, this._position, this._scale);
    }

    /**
     * Generate vector with euler angles from quaternion
     * https://en.wikipedia.org/wiki/Conversion_between_quaternions_and_Euler_angles
     *
     * @param {quat} qt
     * @return {vec3}
     * @protected
     */
    private _quatToEuler(qt: quat): vec3 {
        let y2 = qt[1] * qt[1];

        let t0 = 2.0 * (qt[3] * qt[0] + qt[1] * qt[2]);
        let t1 = 1.0 - 2.0 * (qt[0] * qt[0] + y2);
        let roll = Math.atan2(t0, t1);

        let t2 = 2.0 * (qt[3] * qt[1] - qt[2] * qt[0]);
        t2 = ((t2 > 1.0) ? 1.0 : t2);
        t2 = ((t2 < -1.0) ? -1.0 : t2);
        let pitch = Math.asin(t2);

        let t3 = 2.0 * (qt[3] * qt[2] + qt[0] * qt[1]);
        let t4 = 1.0 - 2.0 * (y2 + qt[2] * qt[2]);
        let yaw = Math.atan2(t3, t4);

        return vec3.set(this._tmpVec3, roll, pitch, yaw);
    }
}
